import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { size, get } from "lodash"
import { Creators as SmartTradeCreators } from "modules/smart-trade/model"
import { Creators as SecuritiesFinderCreators } from "modules/securities-finder/model"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as SmartTradeRuleSearchCreators } from "modules/smart-trade-rule-search/model"
import { getFilter } from "modules/securities-finder/selectors/filter"
import { getModelId } from "modules/smart-trade/selectors/model"
import { getBasketSummaryData, getSmartTradeSummaryData, getSelectedPanelTab, getSelectedGroup, getReadOnly, getStatus, getTabsEnabled, getSelectedDate, GROUP_OPTIONS, getClassificationsTiers } from "./selectors"
import { getSelectedExperimentConfig, getSelectedExperimentPositionSet } from "./selectors/experiment"
import { getCsvRows, getFileName } from "./selectors/csv"
import { getBasketSecurities, getRefInstrumentPositions, getApplyEnabled, getRemovedSecurities, getInstrumentSize, getInstrumentPortfolio, getSmartTradeBasketPositionSet } from "./selectors/positions"
import { getComputedOptimizationRules, getSmartTradeRules, getRulesApplyButtonEnabled, getBasketRules, getExperimentTradeSize } from "./selectors/rules"
import { getSmartTradePositionSet, getSelectedExperiment } from "./selectors/utils"
import { getSecuritiesCount } from "modules/securities-finder-results/selectors"
import { getCurrentSmartTradeValues } from "modules/smart-trade-rule-search/selectors"
import { getAdjustedSecuritiesCount } from "modules/smart-trade/selectors"
import { generateClassificationQuery, classificationsQuery, generateClassificationQueryForContributors, getClassifications } from "helpers/classification"
import { Creators as ProfileCreators } from "model/profileModal"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    modelId: getModelId(state),
    selectedExperiment: getSelectedExperiment(state, props),
    smartTradePositionSet: getSmartTradePositionSet(state, props),
    selectedDate: getSelectedDate(state, props),
    tradeSize: getExperimentTradeSize(state, props),
    classificationsTiers: getClassificationsTiers(state, props),
    classifications: getClassifications(state),
    instrumentPortfolio: getInstrumentPortfolio(state, props),
    smartTradeBasketPositionSet: getSmartTradeBasketPositionSet(state, props),
  }),
  required: ["selectedDate", "modelId"],
  data: [{
    resource: "smartTradeExperimentMeta",
    as: null,
    url: ({ account, selectedExperiment }) => {
      if (!selectedExperiment) {
        return null
      }
      return `/api/${account}/e/${selectedExperiment.id}`
    },
  }, {
    resource: "smartPortfolioPositions",
    passthrough: true,
    url: ({ modelId, smartTradePositionSet, selectedDate, classifications }) => {
      if (!modelId || !smartTradePositionSet || !selectedDate) {
        return null
      }

      return {
        id: `smartPortfolioPositions-${modelId}-${selectedDate}-${JSON.stringify(smartTradePositionSet)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $on, to: $on){
              currency
              composition(scaleFormat: PERCENT_EQUITY_GMV) {
                positions{
                  id
                  economicExposure
                  assetClass
                  country
                  currency
                  ${generateClassificationQueryForContributors("GICS", classifications)}
                  bloombergTicker
                  internalId
                  modelProviderId
                }
              }
              dollarComposition:composition {
                referenceEquity
                gmv
                positions{
                  id
                  country
                  economicExposure
                  averageDailyVolume
                  assetClass
                  bloombergTicker
                  internalId
                  modelProviderId
                }
              }
              exposure {
                factors {
                  name
                  net
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: smartTradePositionSet,
          on: selectedDate,
        },
      }
    },
  }, {
    resource: "smartTradePanelBasket",
    passthrough: true,
    url: ({ modelId, smartTradePositionSet, selectedDate, smartTradeBasketPositionSet }) => {
      if (!modelId || !smartTradePositionSet || !selectedDate || !smartTradeBasketPositionSet) {
        return null
      }

      return {
        id: `smartTradePanelBasket-${modelId}-${selectedDate}-${JSON.stringify(smartTradeBasketPositionSet)}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!) {
          model(id: $modelId) {
            simulation(positionSet: $positionSet, from: $on, to: $on){
              basketSummary:composition(scaleFormat: PERCENT_EQUITY_GMV) {
                summaryStats{
                  average{
                    marketCapitalization
                  }
                }
                gmv
                positionsCount
                concentration{
                  min
                  max
                }
                positions{
                  id
                  economicExposure
                  modelProviderId
                  internalId
                  bloombergTicker
                  assetClass
                  country
                }
              }
            }
          }
        }`,
        variables: {
          modelId,
          positionSet: smartTradeBasketPositionSet,
          on: selectedDate,
        },
      }
    },
  }, {
    resource: "modelCountries",
    as: null,
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/countries`,
  }, {
    resource: "modelCurrencies",
    as: null,
    url: ({ account, modelId }) => `/api/${account}/m/${modelId}/currencies`,
  }, {
    resource: "gicsMetadata",
    as: null,
    url: ({ account, modelId, selectedDate }) => {
      if (!selectedDate) {
        return null
      }
      return `/api/${account}/m/${modelId}/gics?on=${selectedDate}`
    },
  }, {
    resource: "selectedExperimentPositionSet",
    url: ({ selectedExperiment, selectedDate }) => {
      if (!get(selectedExperiment, "positionSet") || !selectedDate) {
        return null
      }
      const { id: topicId, experimentId } = get(selectedExperiment, "positionSet")
      return ({
        id: `selectedExperimentPositionSet-${topicId}-${experimentId}-${selectedDate}`,
        query: `query($topicId: String!, $experimentId: String!, $selectedDate: Date!) {
          researchTopic(id: $topicId) {
            experiment(id: $experimentId) {
              dates(from: $selectedDate, to: $selectedDate) {
                date
                equities {
                  id {
                    ticker
                    mic
                    bloombergTicker
                    modelProviderId
                    internalId
                  }
                  economicExposure
                }
                indexes {
                  id
                  economicExposure
                }
              }
            }
          }
        }`,
        variables: {
          topicId,
          experimentId,
          selectedDate,
        },
      })
    },
  }, {
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    resource: "classificationsValues",
    as: null,
    url: ({ classificationsTiers, selectedDate }) => {
      if (size(classificationsTiers) === 0 || !selectedDate) {
        return null
      }

      // We don't know the depth of each classification, generate each classification sub query according to the tier depth
      const innerQuery = generateClassificationQuery(classificationsTiers)
      return {
        id: `classificationsValues-${selectedDate}-${JSON.stringify(classificationsTiers)}`,
        query: `query {
            ${innerQuery}
          }`,
      }
    },
  },
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    selectedTab: getSelectedPanelTab(state),
    basketSummaryData: getBasketSummaryData(state, props),
    smartTradeSummaryData: getSmartTradeSummaryData(state, props),
    groups: GROUP_OPTIONS,
    selectedGroup: getSelectedGroup(state),
    csvRows: getCsvRows(state, props),
    fileName: getFileName(state, props),
    status: getStatus(state, props),
    computedOptimizationRules: getComputedOptimizationRules(state, props),
    basketRules: getBasketRules(state, props),
    tabsEnabled: getTabsEnabled(state),
    rulesApplyButtonEnabled: getRulesApplyButtonEnabled(state, props),
    positionsApplyEnabled: getApplyEnabled(state),
    basketSecurities: getBasketSecurities(state, props),
    refInstrumentPositions: getRefInstrumentPositions(state, props),
    filters: getFilter(state),
    removedSecurities: getRemovedSecurities(state),
    isReadOnly: getReadOnly(state, props),
    smartTradeRules: getSmartTradeRules(state, props),
    currentSmartTradeValues: getCurrentSmartTradeValues(state, props),
    securitiesCount: getSecuritiesCount(state, props),
    adjustedSecuritiesCount: getAdjustedSecuritiesCount(state, props),
    selectedExperimentConfig: getSelectedExperimentConfig(state, props),
    selectedExperimentPositionSet: getSelectedExperimentPositionSet(state, props),
    instrumentSize: getInstrumentSize(state, props),
  }),
  actions: {
    ...Creators,
    adjustSecurityCount: SmartTradeCreators.adjustSecurityCount,
    setSmartTradeRule: SmartTradeRuleSearchCreators.setSmartTradeRule,
    removeSmartTradeRule: SmartTradeRuleSearchCreators.removeSmartTradeRule,
    resetSmartTradeRules: SmartTradeRuleSearchCreators.resetSmartTradeRules,
    setOptimizationRules: SmartTradeCreators.setRules,
    resetRules: SmartTradeCreators.resetRules,
    loadSecurityFinderFilters: SecuritiesFinderCreators.loadFilters,
    resetSecurityFinderFilters: SecuritiesFinderCreators.resetFilters,
    applyRemovedSecurities: SmartTradeCreators.applyRemovedSecurities,
    openDialog: DialogCreators.open,
    openSecurityProfile2: ProfileCreators.openSecurityProfile2,
  },
})
